import React, { MouseEvent, useCallback, useContext, useRef } from 'react'
import { WidgetContext } from '@/contexts/Widget'
import IWidget from '@/interfaces/IWidget'
import useOverlay from '@/hooks/useOverlay'

type Props = {
  widget: IWidget
  widgetRef: React.RefObject<HTMLDivElement>
}

const Resizable: React.FC<Props> = ({ widget, widgetRef }) => {
  const { dimension, resizing, setResizing, setWidgets, refs, $update } = useContext(WidgetContext)

  const resizableRef = useRef<HTMLDivElement>(null)

  const overlay = useOverlay()

  const onMouseDown = useCallback(({ clientX, clientY }: MouseEvent<HTMLDivElement>) => {
    const element = widgetRef.current as HTMLDivElement

    const rect = element.getBoundingClientRect()

    element.style.width = `${rect.width}px`
    element.style.height = `${rect.height}px`
    element.style.left = `${rect.left}px`
    element.style.top = `${rect.top}px`

    setResizing({
      widget,
      offset: {
        x: rect.width - (clientX - rect.left),
        y: rect.height - (clientY - rect.top),
      },
    })

    const { x, y, colSpan, rowSpan } = widget.layouts[dimension?.breakpoint ?? 'lg']

    overlay.move({
      x,
      y,
      colSpan,
      rowSpan,
    })
  }, [dimension?.breakpoint, overlay, setResizing, widget, widgetRef])

  const onMouseUp = useCallback(() => {
    if (!resizing)
      return

    const { id } = resizing.widget

    setResizing(null)

    const element = widgetRef.current as HTMLDivElement

    element.style.width = 'auto'
    element.style.height = 'auto'
    element.style.left = 'initial'
    element.style.top = 'initial'

    setWidgets(widgets => [
      ...widgets.map(widget => {
        const element = refs[widget.id]?.current

        if (!element)
          return widget

        const { x, y, colSpan, rowSpan } = element.dataset

        widget.layouts[dimension?.breakpoint ?? 'lg'] = {
          x: Number(x),
          y: Number(y),
          colSpan: Number(colSpan),
          rowSpan: Number(rowSpan),
        }

        if (widget.id === id) {
          $update.mutate(widget)
        }

        return widget
      })
    ])

    overlay.hide()
  }, [$update, dimension?.breakpoint, overlay, refs, resizing, setResizing, setWidgets, widgetRef])

  return (
    <div ref={resizableRef} className="resizable" onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
      <div />
    </div>
  )
}

export default Resizable
